import React, { useState } from "react";

import { useViewport } from "@/contexts/ViewportContext";
import { useLocation } from "@/hooks/application/location";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useCreateAddress } from "@/hooks/domain/address";
import { RootNavigation } from "@/navigation/Root";
import { CreateAddressParams } from "@/services/api/address";
import { findAddressByCep } from "@/services/location";

import NewAddressMobile from "./NewAddress.mobile";
import NewAddressWeb from "./NewAddress.web";
import { NewAddresScreenProps } from "./types";

const NewAddress = () => {
  const { isWebMode } = useViewport();
  const { goBack, goToHome } = useAppNavigation<RootNavigation>();
  const { location, getLocationByAddress } = useLocation({});

  const [address, setAddress] = useState<Partial<CreateAddressParams>>(null);
  const [isFetchingAddress, setFetchingAddress] = useState(false);

  const { mutate, isLoading } = useCreateAddress({
    onSuccess: () => {
      goToHome();
    },
  });

  const onCepChange: NewAddresScreenProps["onCepChange"] = async (cep) => {
    setFetchingAddress(true);
    const cepAddress = await findAddressByCep(cep);
    if (cepAddress) {
      setAddress({
        cep: cepAddress.cep,
        street: cepAddress.street,
        neighborhood: cepAddress.neighborhood,
        city: cepAddress.city,
        state: cepAddress.state,
      });
    }
    await getLocationByAddress(cep);
    setFetchingAddress(false);
  };

  const onNumberChange: NewAddresScreenProps["onNumberChange"] = async ({
    street,
    number,
    neighborhood,
  }) => {
    await getLocationByAddress(`${street}, ${number}, ${neighborhood}, ${address?.city}`);
  };

  const onSubmit: NewAddresScreenProps["onSubmit"] = (data) => {
    mutate({
      cep: data.cep,
      city: address.city,
      completion: data.completion,
      location,
      neighborhood: data.neighborhood,
      number: data.number,
      reference: data.reference,
      state: address.state,
      street: data.street,
    });
  };

  const Screen = isWebMode ? NewAddressWeb : NewAddressMobile;

  return (
    <Screen
      address={address}
      location={location}
      isSubmiting={isLoading}
      isFetchingAddress={isFetchingAddress}
      onBack={goBack}
      onCancel={goBack}
      onCepChange={onCepChange}
      onNumberChange={onNumberChange}
      onSubmit={onSubmit}
    />
  );
};

export default NewAddress;
